import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Avatar from '@/components/Avatar';
import PersonIdentity from '@/components/PersonIdentity';
import Sparkline from '@/components/Sparkline';
import {
  Back as BackIcon,
  Search as SearchIcon,
  ExternalLink as ArrowOutIcon,
} from '@/components/icons';
import { useOrg } from '@/context/OrgContext';
import { useData } from '@/lib/useData';
import { formatRelative, safeId } from '@/lib/format';
import type { Repo } from '@/lib/types';

export default function Repositories() {
  const data = useData();
  const { org, label } = useOrg();
  const allRepos = data.repos[org] ?? [];
  const [query, setQuery] = useState('');

  const repos = useMemo(() => {
    if (!query) return allRepos;
    const q = query.toLowerCase();
    return allRepos.filter((r) => r.name.toLowerCase().includes(q) || r.org.toLowerCase().includes(q));
  }, [allRepos, query]);

  // Desktop auto-selects the first repo so the detail pane isn't empty.
  // Mobile starts empty so the list is what the user sees first.
  const isDesktop = typeof window !== 'undefined'
    && window.matchMedia('(min-width: 1024px)').matches;
  const [selectedName, setSelectedName] = useState<string | null>(
    isDesktop ? (allRepos[0]?.full_name ?? null) : null,
  );
  const selected = useMemo(
    () => repos.find((r) => r.full_name === selectedName) ?? null,
    [repos, selectedName],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
      <aside className={[
        'lg:max-h-[calc(100vh-5rem)] lg:flex lg:flex-col lg:sticky lg:top-16',
        selectedName ? 'hidden lg:flex' : 'flex flex-col',
      ].join(' ')}>
        <div className="mb-3 flex items-baseline justify-between">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle">{label(org)}</p>
          <span className="text-[11px] text-fg-subtle num">{repos.length}</span>
        </div>
        <div className="relative mb-2">
          <span className="absolute inset-y-0 left-2.5 flex items-center text-fg-subtle"><SearchIcon /></span>
          <input
            type="search"
            placeholder="Search repositories…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input pl-8"
          />
        </div>
        {repos.length === 0 ? (
          <p className="text-[13px] text-fg-subtle p-4">No repositories match.</p>
        ) : (
          <ul className="flex-1 overflow-y-auto -mx-1 px-1 space-y-0.5">
            {repos.map((r) => (
              <RepoRow key={r.full_name} repo={r}
                active={selected?.full_name === r.full_name}
                onSelect={() => setSelectedName(r.full_name)}
              />
            ))}
          </ul>
        )}
      </aside>
      <section className={[
        'min-w-0',
        selectedName ? 'block' : 'hidden lg:block',
      ].join(' ')}>
        {selected ? (
          <>
            <button
              type="button"
              onClick={() => setSelectedName(null)}
              className="lg:hidden inline-flex items-center gap-1.5 text-[12px] text-fg-muted hover:text-fg mb-3"
            >
              <BackIcon /> Back to repositories
            </button>
            <RepoDetail repo={selected} />
          </>
        ) : (
          <div className="card p-10 text-center text-[13px] text-fg-subtle">Select a repository.</div>
        )}
      </section>
    </div>
  );
}

function RepoRow({ repo, active, onSelect }: { repo: Repo; active: boolean; onSelect: () => void }) {
  return (
    <li>
      <button type="button" onClick={onSelect}
        className={[
          'w-full grid grid-cols-[1fr_auto] items-center gap-3 px-2 py-2 rounded-md text-left transition-colors',
          active ? 'bg-surface-hover' : 'hover:bg-surface-hover',
        ].join(' ')}>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
            <span>{repo.org}</span>
            <span>|</span>
            <span>{formatRelative(repo.last_commit_at)}</span>
          </div>
          <div className="mono text-[13px] font-semibold text-fg truncate">{repo.name}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium num text-fg-muted">
            <span>{repo.commits_window}c</span>
            <span>|</span>
            <span>{repo.contributors_window} contrib</span>
          </div>
        </div>
        <Sparkline values={repo.sparkline} width={52} height={26} />
      </button>
    </li>
  );
}

function RepoDetail({ repo }: { repo: Repo }) {
  return (
    <div className="space-y-5">
      <header className="card p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle">{repo.org}</p>
            <h1 className="mt-1 text-[22px] font-semibold tracking-tight-plus text-fg mono">{repo.name}</h1>
            <div className="mt-2 flex items-center gap-2 text-[11px] text-fg-subtle">
              <span>Default branch <span className="mono text-fg-muted">{repo.default_branch ?? '—'}</span></span>
              <span>|</span>
              <span>Last commit {formatRelative(repo.last_commit_at)}</span>
            </div>
          </div>
          <a href={`https://github.com/${repo.org}/${repo.name}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-surface hover:bg-surface-hover border text-[12px] font-medium text-fg-muted hover:text-fg transition-colors">
            GitHub <ArrowOutIcon />
          </a>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-4">
          <Metric label="Commits | 30d"     value={repo.commits_window} />
          <Metric label="PRs merged | 30d"  value={repo.prs_window} />
          <Metric label="Contributors | 30d" value={repo.contributors_window} />
        </div>
      </header>

      <section className="card p-4">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-3">Activity | last 30 days</div>
        <Sparkline values={repo.sparkline} width={800} height={56} />
      </section>

      <section>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-2">Top contributors | 30d</div>
        {repo.top_contributors.length === 0 ? (
          <div className="card p-5 text-[13px] text-fg-subtle">No contributions in window.</div>
        ) : (
          <div className="card divide-y">
            {repo.top_contributors.map((c) => (
              <Link key={c.canonical_id} to={`/profiles/${safeId(c.canonical_id)}`}
                className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-surface-hover transition-colors">
                <Avatar name={c.display_name} login={c.primary_login} size={28} />
                <PersonIdentity name={c.display_name} login={c.primary_login} size="sm" />
                <span className="ml-auto text-[13px] font-semibold num">{c.n}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[20px] font-semibold num tracking-tight-plus">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mt-0.5">{label}</div>
    </div>
  );
}

